import { useCallback, useEffect, useMemo, useRef } from "react";
import debounce from "lodash/debounce";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import {
  aiPlanFilterBarSx,
  aiPlanFilterFieldSx,
} from "../../constants/regulationDetailsNavTabStyles";
import { useAssessmentPlanFilterOptions } from "../../hooks/useAssessmentPlanFilterOptions";
import { useConfigurableConcepts } from "../../hooks/useConfigurableConcepts";
import {
  dedupeConceptValues,
  usePaginatedConceptValues,
} from "../../hooks/usePaginatedConceptValues";
import AiPlanSelectClearButton from "../AiPlanPage/AiPlanSelectClearButton";
import {
  fetchConditionalPlanOutputConcepts,
  fetchConfigurableConcepts,
  formatConceptCodeToLabel,
  getConceptDisplayLabel,
  normalizePlanTypeApiCode,
} from "../../services/autoTaggingService";
import type { ConceptValue } from "../../services/regulationsService";

const CONCEPT_SEARCH_DEBOUNCE_MS = 300;
const CONCEPT_PAGE_SIZE = 20;
const DIMENSION_PLACEHOLDER = "Select the Dimension";
const VALUES_PLACEHOLDER = "Select the values";

const filterSelectProps = {
  MenuProps: {
    PaperProps: { sx: { maxHeight: 300 } },
  },
} as const;

const placeholderSx = { color: "#9ca3af" } as const;

function dimensionDisplayName(option: {
  code: string;
  name: string;
  display_name?: string;
}): string {
  return (
    getConceptDisplayLabel({
      code: option.code,
      name: option.name,
      display_name: option.display_name ?? "",
    }) || formatConceptCodeToLabel(option.code)
  );
}

export type AiHubDimensionSource = "configurable" | "output";

type AiHubFilterBarProps = Readonly<{
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  conceptValue: string;
  dimensionSource: AiHubDimensionSource;
  onObjectTypeChange: (objectType: string, objectSubType: string) => void;
  onObjectSubTypeChange: (objectSubType: string) => void;
  onConceptCodeChange: (conceptCode: string) => void;
  onConceptValueChange: (conceptValue: string) => void;
}>;

export default function AiHubFilterBar({
  objectType,
  objectSubType,
  conceptCode,
  conceptValue,
  dimensionSource,
  onObjectTypeChange,
  onObjectSubTypeChange,
  onConceptCodeChange,
  onConceptValueChange,
}: AiHubFilterBarProps) {
  const {
    objectTypeOptions,
    getObjectSubTypeOptions,
    planTypesLoading,
  } = useAssessmentPlanFilterOptions(objectType, {
    loadConcepts: false,
    configurableOnly: true,
  });

  const conceptTargetType = normalizePlanTypeApiCode(objectType);
  const filtersNeedObjectType = !conceptTargetType;

  const fetchConcepts =
    dimensionSource === "output"
      ? fetchConditionalPlanOutputConcepts
      : fetchConfigurableConcepts;

  const { conceptOptions } = useConfigurableConcepts(
    conceptTargetType,
    fetchConcepts,
  );

  const objectSubTypeOptions = getObjectSubTypeOptions(objectType);
  const showObjectSubType =
    Boolean(objectType.trim()) && objectSubTypeOptions.length > 0;

  const allDimensionCodes = useMemo(
    () => conceptOptions.map((item) => item.code.trim()).filter(Boolean),
    [conceptOptions],
  );

  const {
    input,
    setInput,
    options,
    loading: valuesLoading,
    hasMoreNext,
    loadingMore,
    loadOptions,
    loadNext,
  } = usePaginatedConceptValues(
    filtersNeedObjectType ? "" : conceptCode,
    CONCEPT_PAGE_SIZE,
    {
      allowEmptyConceptAsAll: !filtersNeedObjectType,
      waitForAllConceptCodes: true,
      allConceptCodes: filtersNeedObjectType ? [] : allDimensionCodes,
    },
  );

  const debouncedLoadOptions = useMemo(
    () =>
      debounce((search?: string) => {
        void loadOptions(search, { force: true });
      }, CONCEPT_SEARCH_DEBOUNCE_MS),
    [loadOptions],
  );

  useEffect(() => () => debouncedLoadOptions.cancel(), [debouncedLoadOptions]);

  const valueQueryRef = useRef(input);
  valueQueryRef.current = input;

  const handleValueInputChange = useCallback(
    (_event: unknown, nextValue: string, reason: string) => {
      if (reason === "reset") return;
      valueQueryRef.current = nextValue;
      setInput(nextValue);
      if (reason !== "input" && reason !== "clear") return;
      if (!nextValue.trim()) {
        debouncedLoadOptions.cancel();
        void loadOptions(undefined, { force: true });
        return;
      }
      debouncedLoadOptions(nextValue);
    },
    [debouncedLoadOptions, loadOptions, setInput],
  );

  const selectedValue = useMemo<ConceptValue | null>(() => {
    if (!conceptValue.trim()) return null;
    const matched = options.find((item) => item.code === conceptValue);
    return {
      id: conceptValue,
      code: conceptValue,
      name: matched?.name ?? conceptValue,
    };
  }, [conceptValue, options]);

  const displayOptions = useMemo(() => {
    const merged =
      selectedValue &&
      !options.some((item) => item.code === selectedValue.code)
        ? [selectedValue, ...options]
        : options;
    return dedupeConceptValues(merged);
  }, [options, selectedValue]);

  const handleClearDimension = useCallback(() => {
    onConceptCodeChange("");
    onConceptValueChange("");
  }, [onConceptCodeChange, onConceptValueChange]);

  return (
    <Box sx={aiPlanFilterBarSx}>
      <TextField
        select
        size="small"
        label="Object Type"
        value={objectType}
        disabled={planTypesLoading}
        sx={aiPlanFilterFieldSx}
        onChange={(event) => {
          onObjectTypeChange(event.target.value, "");
        }}
        SelectProps={filterSelectProps}
      >
        <MenuItem value="" sx={{ display: "none" }} />
        {objectTypeOptions.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>

      {showObjectSubType ? (
        <TextField
          select
          size="small"
          label="Object Sub-Type"
          value={objectSubType}
          sx={aiPlanFilterFieldSx}
          onChange={(event) => onObjectSubTypeChange(event.target.value)}
          SelectProps={filterSelectProps}
        >
          <MenuItem value="" sx={{ display: "none" }} />
          {objectSubTypeOptions.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      <Box sx={{ ...aiPlanFilterFieldSx, minWidth: 220, position: "relative" }}>
        <TextField
          select
          size="small"
          value={conceptCode}
          disabled={filtersNeedObjectType}
          sx={{ minWidth: 220, width: "100%" }}
          onChange={(event) => {
            onConceptCodeChange(event.target.value);
            onConceptValueChange("");
          }}
          inputProps={{ "aria-label": DIMENSION_PLACEHOLDER }}
          SelectProps={{
            ...filterSelectProps,
            displayEmpty: true,
            renderValue: (selected) => {
              const code = String(selected ?? "");
              if (!code) {
                return <Box component="span" sx={placeholderSx}>{DIMENSION_PLACEHOLDER}</Box>;
              }
              const option = conceptOptions.find((item) => item.code === code);
              return option ? dimensionDisplayName(option) : code;
            },
          }}
        >
          <MenuItem value="" sx={{ display: "none" }} />
          {conceptOptions.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {dimensionDisplayName(option)}
            </MenuItem>
          ))}
        </TextField>
        <AiPlanSelectClearButton
          visible={Boolean(conceptCode)}
          disabled={filtersNeedObjectType}
          aria-label="Clear Dimension"
          onClear={handleClearDimension}
        />
      </Box>

      <Autocomplete
        size="small"
        options={displayOptions}
        value={selectedValue}
        inputValue={input}
        disabled={filtersNeedObjectType}
        loading={valuesLoading || loadingMore === "next"}
        filterOptions={(opts) => opts}
        autoComplete={false}
        clearOnBlur={false}
        selectOnFocus
        getOptionKey={(option) => option.code}
        getOptionLabel={(option) => option.name || option.code}
        isOptionEqualToValue={(option, selected) =>
          option.code === selected.code
        }
        onOpen={() => {
          if (!valueQueryRef.current.trim()) {
            void loadOptions(undefined, { force: true });
          }
        }}
        onChange={(_event, nextValue) => {
          onConceptValueChange(nextValue?.code ?? "");
        }}
        onInputChange={handleValueInputChange}
        sx={{
          ...aiPlanFilterFieldSx,
          width: { xs: "100%", sm: 250 },
          minWidth: 250,
        }}
        slotProps={{
          clearIndicator: { "aria-label": "Clear Values" },
        }}
        ListboxProps={{
          style: { maxHeight: 300 },
          onScroll: (event) => {
            const listbox = event.currentTarget;
            if (listbox.scrollTop <= 0) return;
            const nearBottom =
              listbox.scrollTop + listbox.clientHeight >=
              listbox.scrollHeight - 40;
            if (nearBottom && hasMoreNext && loadingMore !== "next") {
              void loadNext();
            }
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={VALUES_PLACEHOLDER}
            inputProps={{
              ...params.inputProps,
              "aria-label": VALUES_PLACEHOLDER,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {valuesLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
